import React, { useState } from 'react';
import { FiAlertTriangle, FiInfo, FiSettings } from 'react-icons/fi';
import { MdClose } from 'react-icons/md';
import * as S from './DeviceModal.styles';
import type { Device, DeviceStatusPayload } from './index';

interface DeviceAlarm {
  id: string;
  type: 'critical' | 'warning' | 'info';
  message: string;
  timestamp: string;
  resolved?: string;
}

interface MQTTAlarmData {
  activeAlarms: DeviceAlarm[];
  alarmHistory: DeviceAlarm[];
}

interface DeviceModalProps {
  device: Device;
  isOpen: boolean;
  onClose: () => void;
  statusData?: DeviceStatusPayload;
  statusLoading?: boolean;
}

const DeviceModal: React.FC<DeviceModalProps> = ({ device, isOpen, onClose, statusData, statusLoading = false }) => {
  const [activeTab, setActiveTab] = useState<'status' | 'installation' | 'alarms'>('status');
  const [activeStatusSubTab, setActiveStatusSubTab] = useState<'dispositivo' | 'comunicacao' | 'es' | 'coleta' | 'bateria'>('dispositivo');
  const [showStatusJson, setShowStatusJson] = useState(false);
  const [showInstallationJson, setShowInstallationJson] = useState(false);

  const statusSection = statusData?.status;
  const installationSection = statusSection?.instalacao;
  const comunicacao = statusSection?.comunicacao;
  const portas = statusSection?.portas;
  const bateria = statusSection?.bateria;
  const dispositivo = statusSection?.dispositivo;
  const sensors = portas?.sensores_conectados || [];
  const deviceIdentifierLabel = statusData?.isBottomUp ? 'Serial Number:' : 'MAC:';
  const coordinates = installationSection?.coordenadas;
  const hasCoordinateValue = (value?: string | number | null) => {
    return !(value === undefined || value === null || value === '');
  };
  const hasCoordinates = hasCoordinateValue(coordinates?.lat) && hasCoordinateValue(coordinates?.lon);
  const simCards = [
    { label: 'SIM 1', data: comunicacao?.imei1 },
  ];
  const isLoadingData = Boolean(statusLoading && !statusData);

  const formatDate = (dateString?: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleString('pt-BR');
  };

  const formatTimeSince = (dateString?: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '';

    const diffMs = Date.now() - date.getTime();
    if (diffMs < 0) return '';

    const totalMinutes = Math.floor(diffMs / (1000 * 60));
    if (totalMinutes < 1) return 'menos de 1 minuto';

    const days = Math.floor(totalMinutes / (60 * 24));
    const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
    const minutes = totalMinutes % 60;

    const parts: string[] = [];
    if (days) parts.push(`${days} dia${days > 1 ? 's' : ''}`);
    if (hours) parts.push(`${hours} hora${hours > 1 ? 's' : ''}`);
    if (!days && minutes) parts.push(`${minutes} minuto${minutes > 1 ? 's' : ''}`);

    return parts.length ? parts.join(' e ') : '';
  };

  const formatTextValue = (value?: string | number | null) => {
    if (value === null || value === undefined || value === '') {
      return '';
    }
    return typeof value === 'number' ? value.toString() : value;
  };

  const formatPortCount = (value?: number | null) => {
    if (value === null || value === undefined || Number.isNaN(value)) return '0';
    return value.toString();
  };

  const formatSecondsValue = (value?: number | null) => {
    if (value === null || value === undefined || Number.isNaN(value)) return '';
    return `${value} segundos`;
  };

  const formatDbmValue = (value?: number | null) => {
    if (value === null || value === undefined || Number.isNaN(value)) return '';
    return `${value} dBm`;
  };

  const formatVoltageValue = (value?: number | null) => {
    if (value === null || value === undefined || Number.isNaN(value)) return '';
    return `${value.toFixed(2)} V`;
  };

  const formatPercentageValue = (value?: number | null) => {
    if (value === null || value === undefined || Number.isNaN(value)) return '';
    return `${value}%`;
  };

  const getSensorTitle = (sensor: { channel?: string; type?: string; model?: string }) => {
    const pieces = [sensor.type, sensor.model].filter(Boolean);
    if (pieces.length === 0 && sensor.channel) {
      pieces.push(`Canal ${sensor.channel}`);
    }
    return pieces.join(' - ');
  };

  const getSensorChannelLabel = (sensor: { channel?: string }) => {
    if (!sensor.channel) return '';
    return `Canal ${sensor.channel}`;
  };

  const alarmData: MQTTAlarmData = {
    activeAlarms: device?.batteryLevel && device.batteryLevel < 30 ? [{
      id: '1',
      type: 'warning',
      message: 'Bateria baixa: ' + device.batteryLevel + '%',
      timestamp: new Date().toISOString(),
    }] : [],
    alarmHistory: [
      {
        id: '1',
        type: 'critical',
        message: 'Perda de comunicação',
        timestamp: '2024-11-20T14:30:00Z',
        resolved: '2024-11-20T14:35:00Z',
      },
      {
        id: '2',
        type: 'warning',
        message: 'Temperatura alta: 45°C',
        timestamp: '2024-11-19T09:15:00Z',
        resolved: '2024-11-19T10:00:00Z',
      },
    ],
  };

  if (!isOpen) return null;

  const getAlarmIcon = (type: string) => {
    switch (type) {
      case 'critical': return '🔴';
      case 'warning': return '⚠️';
      default: return 'ℹ️';
    }
  };

  return (
    <S.ModalOverlay onClick={onClose}>
      <S.ModalContainer onClick={(e: React.MouseEvent) => e.stopPropagation()}>
        <S.ModalHeader>
          <div>
            <S.DeviceName>{device.name}</S.DeviceName>
            <S.DeviceClient>{device.client || ''}</S.DeviceClient>
          </div>
          <S.CloseButton onClick={onClose}>
            <MdClose size={24} />
          </S.CloseButton>
        </S.ModalHeader>

        <S.TabContainer>
          <S.TabButton 
            $isActive={activeTab === 'status'}
            onClick={() => setActiveTab('status')}
          >
            <FiInfo size={16} />
            Status
          </S.TabButton>
          <S.TabButton 
            $isActive={activeTab === 'installation'}
            onClick={() => setActiveTab('installation')}
          >
            <FiSettings size={16} />
            Instalação
          </S.TabButton>
          <S.TabButton 
            $isActive={activeTab === 'alarms'}
            onClick={() => setActiveTab('alarms')}
            style={{ display: 'none' }}
            aria-hidden="true"
          >
            <FiAlertTriangle size={16} />
            Alarmes
          </S.TabButton>
        </S.TabContainer>

        <S.TabContent>
          {isLoadingData ? (
            <S.LoadingContainer>
              <S.LoadingSpinner />
              <p>Carregando dados do dispositivo...</p>
            </S.LoadingContainer>
          ) : (
            <>
              {activeTab === 'status' && (
                <S.StatusTab>
                  <S.SubTabContainer>
                    <S.SubTabButton 
                      $isActive={activeStatusSubTab === 'dispositivo'}
                      onClick={() => setActiveStatusSubTab('dispositivo')}
                    >
                      Dispositivo
                    </S.SubTabButton>
                    <S.SubTabButton 
                      $isActive={activeStatusSubTab === 'comunicacao'}
                      onClick={() => setActiveStatusSubTab('comunicacao')}
                    >
                      Comunicação
                    </S.SubTabButton>
                    <S.SubTabButton 
                      $isActive={activeStatusSubTab === 'es'}
                      onClick={() => setActiveStatusSubTab('es')}
                    >
                      Portas
                    </S.SubTabButton>
                    <S.SubTabButton 
                      $isActive={activeStatusSubTab === 'coleta'}
                      onClick={() => setActiveStatusSubTab('coleta')}
                    >
                      Coleta e Envio
                    </S.SubTabButton>
                    <S.SubTabButton 
                      $isActive={activeStatusSubTab === 'bateria'}
                      onClick={() => setActiveStatusSubTab('bateria')}
                    >
                      Bateria
                    </S.SubTabButton>
                  </S.SubTabContainer>

                  <S.ToggleContainer>
                    <S.MacAddressDisplay>
                      <S.MacAddressLabel>{deviceIdentifierLabel}</S.MacAddressLabel>
                      <S.MacAddressValue>
                        {formatTextValue(dispositivo?.mac || dispositivo?.mac_address || dispositivo?.numero_serie || device.macAddress)}
                      </S.MacAddressValue>
                    </S.MacAddressDisplay>
                    <S.ToggleLabel>Visualização:</S.ToggleLabel>
                    <S.ToggleWrapper>
                      <S.ToggleButton 
                        $isActive={!showStatusJson}
                        onClick={() => setShowStatusJson(false)}
                      >
                        Visual
                      </S.ToggleButton>
                      <S.ToggleButton 
                        $isActive={showStatusJson}
                        onClick={() => setShowStatusJson(true)}
                      >
                        JSON
                      </S.ToggleButton>
                    </S.ToggleWrapper>
                  </S.ToggleContainer>
                  
                  {activeStatusSubTab === 'dispositivo' ? (
                    <>
                      {!showStatusJson ? (
                        <S.VisualContent>
                          <S.InfoSection>
                            <S.InfoRow>
                              <S.InfoLabel>Número de Série:</S.InfoLabel>
                              <S.InfoValue>{formatTextValue(dispositivo?.numero_serie)}</S.InfoValue>
                            </S.InfoRow>
                            <S.InfoRow>
                              <S.InfoLabel>Fabricante:</S.InfoLabel>
                              <S.InfoValue>{formatTextValue(dispositivo?.fabricante)}</S.InfoValue>
                            </S.InfoRow>
                            <S.InfoRow>
                              <S.InfoLabel>Modelo de Hardware:</S.InfoLabel>
                              <S.InfoValue>{formatTextValue(dispositivo?.modelo)}</S.InfoValue>
                            </S.InfoRow>
                            <S.InfoRow>
                              <S.InfoLabel>Versão de Firmware:</S.InfoLabel>
                              <S.InfoValue>{formatTextValue(bateria?.versao_firmware)}</S.InfoValue>
                            </S.InfoRow>
                          </S.InfoSection>
                        </S.VisualContent>
                      ) : (
                        <S.JsonContainer>
                          <S.JsonContent>
                            {JSON.stringify(dispositivo || {}, null, 2)}
                          </S.JsonContent>
                        </S.JsonContainer>
                      )}
                    </>
                  ) : activeStatusSubTab === 'comunicacao' ? (
                    <>
                      {!showStatusJson ? (
                        <S.VisualContent>
                          <S.InfoSection>
                            {simCards.map((simCard) => (
                              <React.Fragment key={simCard.label}>
                                <S.InfoRow>
                                  <S.InfoLabel>IMEI {simCard.label}:</S.InfoLabel>
                                  <S.InfoValue>{formatTextValue(simCard.data?.slot)}</S.InfoValue>
                                </S.InfoRow>
                                <S.InfoRow>
                                  <S.InfoLabel>ICCID {simCard.label}:</S.InfoLabel>
                                  <S.InfoValue>{formatTextValue(simCard.data?.simcard_num)}</S.InfoValue>
                                </S.InfoRow>
                                <S.InfoRow>
                                  <S.InfoLabel>Operadora {simCard.label}:</S.InfoLabel>
                                  <S.InfoValue>{formatTextValue(simCard.data?.operadora)}</S.InfoValue>
                                </S.InfoRow>
                                <S.InfoRow>
                                  <S.InfoLabel>Sinal {simCard.label} (dBm):</S.InfoLabel>
                                  <S.InfoValue>{formatDbmValue(simCard.data?.sinal_operadora_dbm)}</S.InfoValue>
                                </S.InfoRow>
                              </React.Fragment>
                            ))}
                          </S.InfoSection>
                        </S.VisualContent>
                      ) : (
                        <S.JsonContainer>
                          <S.JsonContent>
                            {JSON.stringify(comunicacao || {}, null, 2)}
                          </S.JsonContent>
                        </S.JsonContainer>
                      )}
                    </>
                  ) : activeStatusSubTab === 'es' ? (
                    <>
                      {!showStatusJson ? (
                        <S.VisualContent>
                          <S.InfoSection>
                            <S.InfoRow>
                              <S.InfoLabel>Portas Analógicas Ativas:</S.InfoLabel>
                              <S.InfoValue>{formatPortCount(portas?.portas_analogicas_ativas)}</S.InfoValue>
                            </S.InfoRow>
                            <S.InfoRow>
                              <S.InfoLabel>Portas Digitais Ativas:</S.InfoLabel>
                              <S.InfoValue>{formatPortCount(portas?.portas_digitais_ativas)}</S.InfoValue>
                            </S.InfoRow>
                          </S.InfoSection>
                          <S.SensorSection>
                            <S.SensorTitle>Sensores Conectados</S.SensorTitle>
                            <S.SensorGrid>
                              {sensors.map((sensor, index) => (
                                <S.SensorCard key={`${sensor.channel || 'sensor'}-${index}`}>
                                  <S.SensorName>{getSensorTitle(sensor)}</S.SensorName>
                                  <S.SensorStatus $isActive={true}>{getSensorChannelLabel(sensor)}</S.SensorStatus>
                                </S.SensorCard>
                              ))}
                            </S.SensorGrid>
                          </S.SensorSection>
                        </S.VisualContent>
                      ) : (
                        <S.JsonContainer>
                          <S.JsonContent>
                            {JSON.stringify(portas || {}, null, 2)}
                          </S.JsonContent>
                        </S.JsonContainer>
                      )}
                    </>
                  ) : activeStatusSubTab === 'coleta' ? (
                    <>
                      {!showStatusJson ? (
                        <S.VisualContent>
                          <S.InfoSection>
                            <S.InfoRow>
                              <S.InfoLabel>Período de Varredura:</S.InfoLabel>
                              <S.InfoValue>{formatSecondsValue(statusSection?.coleta?.periodo_varredura)}</S.InfoValue>
                            </S.InfoRow>
                            <S.InfoRow>
                              <S.InfoLabel>Período de Envio:</S.InfoLabel>
                              <S.InfoValue>{formatSecondsValue(statusSection?.coleta?.periodo_envio)}</S.InfoValue>
                            </S.InfoRow>
                            <S.InfoRow>
                              <S.InfoLabel>Tempo desde Último Boot:</S.InfoLabel>
                              <S.InfoValue>{formatTimeSince(bateria?.data_hora_ultima_conexao)}</S.InfoValue>
                            </S.InfoRow>
                            <S.InfoRow>
                              <S.InfoLabel>Último Envio ao Broker:</S.InfoLabel>
                              <S.InfoValue>{formatDate(bateria?.data_hora_envio)}</S.InfoValue>
                            </S.InfoRow>
                          </S.InfoSection>
                        </S.VisualContent>
                      ) : (
                        <S.JsonContainer>
                          <S.JsonContent>
                            {JSON.stringify(statusSection?.coleta || {}, null, 2)}
                          </S.JsonContent>
                        </S.JsonContainer>
                      )}
                    </>
                  ) : (
                    <>
                      {!showStatusJson ? (
                        <S.VisualContent>
                          <S.InfoSection>
                            <S.InfoRow>
                              <S.InfoLabel>Tensão da Bateria:</S.InfoLabel>
                              <S.InfoValue>{formatVoltageValue(bateria?.tensao)}</S.InfoValue>
                            </S.InfoRow>
                            <S.InfoRow>
                              <S.InfoLabel>Estado de Carga:</S.InfoLabel>
                              <S.InfoValue>{formatPercentageValue(bateria?.carga_percentual)}</S.InfoValue>
                            </S.InfoRow>
                          </S.InfoSection>
                        </S.VisualContent>
                      ) : (
                        <S.JsonContainer>
                          <S.JsonContent>
                            {JSON.stringify(bateria || {}, null, 2)}
                          </S.JsonContent>
                        </S.JsonContainer>
                      )}
                    </>
                  )}
                </S.StatusTab>
              )}

              {activeTab === 'installation' && (
                <S.InstallationTab>
                  <S.ToggleContainer>
                    <S.ToggleLabel>Visualização:</S.ToggleLabel>
                    <S.ToggleWrapper>
                      <S.ToggleButton 
                        $isActive={!showInstallationJson}
                        onClick={() => setShowInstallationJson(false)}
                      >
                        Visual
                      </S.ToggleButton>
                      <S.ToggleButton 
                        $isActive={showInstallationJson}
                        onClick={() => setShowInstallationJson(true)}
                      >
                        JSON
                      </S.ToggleButton>
                    </S.ToggleWrapper>
                  </S.ToggleContainer>
                  
                  {!showInstallationJson ? (
                    <S.VisualContent>
                      <S.InfoSection>
                        <S.InfoRow>
                          <S.InfoLabel>Localização:</S.InfoLabel>
                          <S.InfoValue>{formatTextValue(installationSection?.localizacao)}</S.InfoValue>
                        </S.InfoRow>
                        <S.InfoRow>
                          <S.InfoLabel>Coordenadas:</S.InfoLabel>
                          <S.InfoValue>
                            {hasCoordinates ? (
                              <S.MapsLink
                                href={`https://www.google.com/maps?q=${coordinates?.lat},${coordinates?.lon}`}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {coordinates?.lat}, {coordinates?.lon}
                              </S.MapsLink>
                            ) : (
                              ''
                            )}
                          </S.InfoValue>
                        </S.InfoRow>
                        <S.InfoRow>
                          <S.InfoLabel>Data de Instalação:</S.InfoLabel>
                          <S.InfoValue>{formatDate(installationSection?.data_instalacao)}</S.InfoValue>
                        </S.InfoRow>
                        <S.InfoRow>
                          <S.InfoLabel>Número de Série:</S.InfoLabel>
                          <S.InfoValue>{formatTextValue(dispositivo?.numero_serie)}</S.InfoValue>
                        </S.InfoRow>
                        <S.InfoRow>
                          <S.InfoLabel>Firmware:</S.InfoLabel>
                          <S.InfoValue>{formatTextValue(installationSection?.firmware)}</S.InfoValue>
                        </S.InfoRow>
                        <S.InfoRow>
                          <S.InfoLabel>Hardware:</S.InfoLabel>
                          <S.InfoValue>{formatTextValue(installationSection?.hardware)}</S.InfoValue>
                        </S.InfoRow>
                      </S.InfoSection>
                    </S.VisualContent>
                  ) : (
                    <S.JsonContainer>
                      <S.JsonContent>
                        {JSON.stringify(installationSection || {}, null, 2)}
                      </S.JsonContent>
                    </S.JsonContainer>
                  )}
                </S.InstallationTab>
              )}

              {activeTab === 'alarms' && (
                <S.AlarmsTab>
                  {alarmData.activeAlarms.length > 0 && (
                    <S.AlarmSection>
                      <S.AlarmSectionTitle>Alarmes Ativos</S.AlarmSectionTitle>
                      {alarmData.activeAlarms.map((alarm) => (
                        <S.AlarmItem key={alarm.id} $type={alarm.type}>
                          <S.AlarmIcon>{getAlarmIcon(alarm.type)}</S.AlarmIcon>
                          <S.AlarmContent>
                            <S.AlarmMessage>{alarm.message}</S.AlarmMessage>
                            <S.AlarmTime>{formatDate(alarm.timestamp)}</S.AlarmTime>
                          </S.AlarmContent>
                        </S.AlarmItem>
                      ))}
                    </S.AlarmSection>
                  )}

                  <S.AlarmSection>
                    <S.AlarmSectionTitle>Histórico de Alarmes</S.AlarmSectionTitle>
                    {alarmData.alarmHistory.length === 0 ? (
                      <S.NoAlarms>Nenhum alarme no histórico</S.NoAlarms>
                    ) : (
                      alarmData.alarmHistory.map((alarm) => (
                        <S.AlarmItem key={alarm.id} $type={alarm.type} $resolved={!!alarm.resolved}>
                          <S.AlarmIcon>{getAlarmIcon(alarm.type)}</S.AlarmIcon>
                          <S.AlarmContent>
                            <S.AlarmMessage>{alarm.message}</S.AlarmMessage>
                            <S.AlarmTime>
                              Iniciado: {formatDate(alarm.timestamp)}
                              {alarm.resolved && (
                                <span> • Resolvido: {formatDate(alarm.resolved)}</span>
                              )}
                            </S.AlarmTime>
                          </S.AlarmContent>
                        </S.AlarmItem>
                      ))
                    )}
                  </S.AlarmSection>
                </S.AlarmsTab>
              )}
            </>
          )}
        </S.TabContent>
      </S.ModalContainer>
    </S.ModalOverlay>
  );
};

export default DeviceModal;